import { type FC, type MouseEvent, type PropsWithChildren, useEffect } from 'react';

import { skipToMainStyle } from './SkipToMain.css';
import { useI18nTranslation } from '../../providers/i18n';
import { ButtonLink } from '../ButtonLink/ButtonLink';

const MAIN_ID = 'main';

/** Stable id of the skip link, so callers can find it without depending on its label. */
export const SKIP_TO_MAIN_LINK_ID = 'skip-to-main';

interface SkipLinkProps {
  /**
   * Optional element id to skip to when it is the first content in `SkipToMain.Main`.
   * Falls back to `main` when the element is missing or sits below other content.
   */
  targetId?: string;
}

/** True when `element` is the first content in `container` (only ancestors wrap it). */
const isAtStartOf = (element: HTMLElement, container: HTMLElement): boolean => {
  if (element === container || !container.contains(element)) {
    return false;
  }

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();

  while (node) {
    if (node === element) {
      return true;
    }

    if (node instanceof HTMLElement && !node.contains(element)) {
      return false;
    }

    node = walker.nextNode();
  }

  return false;
};

const getSkipTarget = (targetId?: string): HTMLElement | null => {
  const main = document.getElementById(MAIN_ID);

  if (targetId) {
    const preferred = document.getElementById(targetId);
    if (preferred && main && isAtStartOf(preferred, main)) {
      return preferred;
    }
  }

  return main;
};

const Main: FC<PropsWithChildren<{ className?: string }>> = ({ children, className }) => {
  return (
    <main id={MAIN_ID} tabIndex={-1} className={className}>
      {children}
    </main>
  );
};

const SkipLink: FC<SkipLinkProps> = ({ targetId }) => {
  const buttonText = useI18nTranslation('skipToMain');
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const mainCounter = document.querySelectorAll(`[id="${MAIN_ID}"]`).length;
      if (mainCounter !== 1) {
        // eslint-disable-next-line no-console
        console.error(
          `Error: The <SkipToMain.SkipLink> button requires one item to be identified as "main", but this amount was found: ${mainCounter}. Use <SkipToMain.Main> to wrap the main content that can be skipped to, and make sure it is only rendered once per page. Check the docs of \`SkipToMain\` for more info.`,
        );
      }
    }
  }, []);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = getSkipTarget(targetId);
    if (!target) return;
    event.preventDefault();
    target.focus({ preventScroll: false });
    window.history.replaceState(null, '', `#${target.id}`);
  };

  return (
    <div className={skipToMainStyle}>
      <ButtonLink action="secondary" href={`#${MAIN_ID}`} id={SKIP_TO_MAIN_LINK_ID} onClick={handleClick}>
        {buttonText}
      </ButtonLink>
    </div>
  );
};

export const SkipToMain = { SkipLink, Main };

SkipLink.displayName = 'SkipToMain.SkipLink';
